import React, { ChangeEvent, FormEvent, useEffect, useReducer, useState } from 'react';
import { text } from '../../data';
import {
	getGreaseDetailsByFilter,
	greaseLubricationContent,
} from '../../../components/bearing-search/api/api';
import SelectDropdown from '../../../components/bearing-search/components/forms/SelectDropdown';
import greaseSelectionReducer, { initialGreaseSelectionState } from './reducer';
import {generateId, toggleImportantNote, toggleNextTools} from '../../utils/engineering-tools';
import { SelectOption } from '../../types/types';
import { GreaseSelectionPayload, TimkenGreaseDetail } from '../../../components/bearing-search/types/types';
import GreaseSelectionResults from '../../components/grease-selection-results/GreaseSelectionResults';
import { trackProductResults, trackStart, updateActionParams } from '../../utils/analytics';

const GreaseSelection: React.FC = () => {
	const formDescription = greaseLubricationContent?.selectionDesc
		? { __html: greaseLubricationContent.selectionDesc }
		: undefined;

	const footerNote = greaseLubricationContent?.selectionFooter
		? { __html: greaseLubricationContent.selectionFooter }
		: undefined;

	const [ stamp, setStamp ] = useState( '' );
	const [ results, setResults ] = useState<TimkenGreaseDetail[]>( [] );
	const [ isSearched, setIsSearched ] = useState( false );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ formState, dispatch ] = useReducer(
		greaseSelectionReducer,
		initialGreaseSelectionState,
	);

	useEffect( () => {
		trackStart( 'greaseSearch' );
		
		// clear saved selected bearing. This resets the Save Bearing dropdown
		const event = new CustomEvent('bearingSelectedEvent', {
			detail: { hideDropdown: true }
		});
		setTimeout(() => {
			document.dispatchEvent(event);
		}, 100); // add timeout since the component fires the event before the dropdown listener is set
	}, [] );

	// Show / hide recommended tools
	useEffect( () => {
		toggleNextTools( isSearched && ! isResultsLoading );

		return () => {
			toggleNextTools( false );
		};
	}, [ isSearched, isResultsLoading ] );

	const clearSearch = () => {
		setIsSearched( false );

		// Update random generated stamp to force re-render of select form (to un-selected state)
		setStamp( new Date().getTime().toString() );

		dispatch( {
			type: 'reset',
			payload: initialGreaseSelectionState,
		} );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();
		setIsSearched( true );
		setIsResultsLoading( true );

		if ( formState.requestData ) {
			const request: GreaseSelectionPayload = formState.requestData;

			getGreaseDetailsByFilter( formState.requestData )
				.then( ( response ) => {
					trackProductResults( 'greaseSearch', request, response.greaseDetails.length );
					updateActionParams( 'greaseSearch' );
					setResults( response.greaseDetails );

					if (response.header.messages?.length) {
						toggleImportantNote( false );
					} else {
						toggleImportantNote( true );
					}

					document.querySelector('.t-engineering-tools__results').scrollIntoView({
						behavior: 'smooth'
					});

				} )
				.finally( () => setIsResultsLoading( false ) );
		}
	};

	return (
		<div>
			<form onSubmit={handleSubmit}>
				{!! formDescription && (
					<p
						className="t-engineering-tools__description"
						dangerouslySetInnerHTML={formDescription}
					></p>
				)}

				<p className="c-form__disclaimer">
					{text.greaseSelectionNote}
				</p>

				<p>
					<span className="c-form__asterisk">*</span>
					{text.requiredMessage}
				</p>

				<div className="row form-row">
					<div className="col-md-3">
						<SelectDropdown
							key={stamp}
							id="grease_selection_segment"
							name="segment"
							required={true}
							placeholder={
								! formState.segment
									? `${ text.select } ${ text.segment }`
									: undefined
							}
							label={text.segment}
							value={formState.segment}
							options={formState.segmentOptions.map( ( item ) => {
								return {
									id: `grease_selection_segment_${ generateId( item ) }`,
									value: item,
									label: item,
								} as SelectOption<string>;
							} )}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_segment',
									payload: event.target.value,
								} )
							}
						/>
					</div>

					<div className="col-md-3">
						<SelectDropdown
							key={`${ stamp }_${ formState.segment }`}
							id="grease_selection_equipment"
							name="equipment"
							required={true}
							placeholder={
								! formState.equipment
									? `${ text.select } ${ text.equipment }`
									: undefined
							}
							label={text.equipment}
							value={formState.equipment}
							options={formState.equipmentOptions.map( ( item ) => {
								return {
									id: `grease_selection_equipment_${ generateId( item ) }`,
									value: item,
									label: item,
								} as SelectOption<string>;
							} )}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_equipment',
									payload: event.target.value,
								} )
							}
						/>
					</div>

					<div className="col-md-3">
						<SelectDropdown
							key={`${ stamp }_${ formState.segment }_${ formState.equipment }`}
							id="grease_selection_application"
							name="application"
							required={true}
							placeholder={
								! formState.application
									? `${ text.select } ${ text.application }`
									: undefined
							}
							label={text.application}
							value={formState.application}
							options={formState.applicationOptions.map( ( item ) => {
								return {
									id: `grease_selection_application_${ generateId( item ) }`,
									value: item,
									label: item,
								} as SelectOption<string>;
							} )}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_application',
									payload: event.target.value,
								} )
							}
						/>
					</div>
				</div>

				<div className="t-engineering-tools__search-actions">
					<button
						className="btn btn--primary"
						type="submit"
						disabled={! formState.requestData}
					>
						{text.submit}
					</button>

					<button
						className="btn btn--secondary"
						type="button"
						onClick={() => clearSearch()}
					>
						{text.clear}
					</button>
				</div>
			</form>

			{isSearched && (
				<GreaseSelectionResults data={results} loading={isResultsLoading} />
			)}

			{isSearched && ! isResultsLoading && footerNote && (
				<div
					 className="t-engineering-tools__notes js-engineering-tools-notes"
					 id="js-engineering-tools-notes"
					 dangerouslySetInnerHTML={footerNote}></div>
			)}
		</div>
	);
};

export default GreaseSelection;
